import { useState, useCallback, useRef } from 'react';
import { useTrackActivity } from '@/hooks/useActivityTracker';
import { useTranslation } from 'react-i18next';
import { Sparkles, RefreshCw, Lightbulb, Target, PieChart, MessageSquare, ChevronDown, ChevronUp, Building2, Trophy, TrendingUp, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart as RechartsPieChart, Pie } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface WinnerInfo {
  lotNumber?: string;
  name: string;
  vatNumber: string;
}

interface TedResult {
  id: string;
  ted_notice_id: string;
  title: string;
  description: string;
  buyer_name: string;
  country: string;
  cpv_codes: string[];
  estimated_value: number | null;
  currency: string;
  publication_date: string;
  deadline_date: string | null;
  notice_type?: string;
  winner_name?: string;
  winners?: WinnerInfo[];
}

interface BuyerEntry { name: string; count: number; note?: string }
interface WinnerEntry { name: string; count: number; note?: string }

interface AIAnalysis {
  summary: string;
  keyInsights: string[];
  topOpportunities: { title: string; reason: string }[];
  sectorBreakdown: Record<string, number>;
  avgValueRange: string;
  buyerAnalysis?: {
    topBuyers: BuyerEntry[];
    concentration?: string;
  };
  winnerAnalysis?: {
    topWinners: WinnerEntry[];
    competitiveness?: string;
  };
  recommendations: string[];
}

interface SearchResultsAIProps {
  results: TedResult[];
  isSearching?: boolean;
  searchQuery?: string;
}

export default function SearchResultsAI({ results, isSearching, searchQuery }: SearchResultsAIProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const trackActivity = useTrackActivity();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const lastAnalyzedRef = useRef<string>('');
  const buyerChartRef = useRef<HTMLDivElement>(null);
  const winnerChartRef = useRef<HTMLDivElement>(null);
  const sectorChartRef = useRef<HTMLDivElement>(null);

  const analyzeResults = useCallback(async (force = false) => {
    if (results.length === 0) return;
    const resultHash = results.slice(0, 25).map(r => r.id).join(',');
    if (!force && lastAnalyzedRef.current === resultHash && analysis) return;
    
    setStatus('loading');
    setErrorMessage('');
    
    try {
      const { data, error } = await supabase.functions.invoke('summarize-search-results', {
        body: { 
          results: results.slice(0, 25),
          language: i18n.language?.split('-')[0] || 'it'
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.analysis) {
        setAnalysis(data.analysis);
        setStatus('success');
        lastAnalyzedRef.current = resultHash;
      } else {
        throw new Error('No analysis returned');
      }
    } catch (err: any) {
      console.error('AI Analysis error:', err);
      setStatus('error');
      if (err.status === 429 || err.message?.includes('429')) {
        setErrorMessage(t('search.aiAnalysis.rateLimitError', 'Limite richieste raggiunto. Riprova tra poco.'));
      } else if (err.status === 402 || err.message?.includes('402')) {
        setErrorMessage(t('search.aiAnalysis.creditsError', 'Crediti AI esauriti.'));
      } else {
        setErrorMessage(err.message || t('search.aiAnalysis.error', 'Errore durante l\'analisi'));
      }
      toast({
        variant: "destructive",
        title: t('search.aiAnalysis.error', 'Errore analisi AI'),
        description: err.message || 'Si è verificato un errore',
      });
    }
  }, [results, analysis, i18n.language, t, toast]);

  const svgToDataUrl = useCallback(async (containerRef: React.RefObject<HTMLDivElement | null>, width = 500, height = 200): Promise<string | null> => {
    const container = containerRef.current;
    if (!container) return null;
    const svg = container.querySelector('svg');
    if (!svg) return null;
    const clone = svg.cloneNode(true) as SVGElement;
    clone.setAttribute('width', String(width));
    clone.setAttribute('height', String(height));
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // Inline computed styles for text elements
    const origTexts = svg.querySelectorAll('text');
    const cloneTexts = clone.querySelectorAll('text');
    origTexts.forEach((orig, i) => {
      const cs = getComputedStyle(orig);
      cloneTexts[i]?.setAttribute('fill', cs.fill || '#333');
      cloneTexts[i]?.setAttribute('font-size', cs.fontSize || '11px');
    });
    // Inline computed fill for rects/paths
    const origShapes = svg.querySelectorAll('rect, path, circle');
    const cloneShapes = clone.querySelectorAll('rect, path, circle');
    origShapes.forEach((orig, i) => {
      const cs = getComputedStyle(orig);
      if (cs.fill && cs.fill !== 'none') cloneShapes[i]?.setAttribute('fill', cs.fill);
      if (cs.stroke && cs.stroke !== 'none') cloneShapes[i]?.setAttribute('stroke', cs.stroke);
      if (cs.opacity) cloneShapes[i]?.setAttribute('opacity', cs.opacity);
    });
    const data = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width * 2;
        canvas.height = height * 2;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }, []);

  const exportToPdf = useCallback(async () => {
    if (!analysis) return;
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const doc = new jsPDF();
      const margin = 14;
      let y = 20;

      doc.setFontSize(16);
      doc.text(t('search.aiAnalysis.title', 'Analisi AI'), margin, y);
      y += 10;

      // Summary
      doc.setFontSize(10);
      const summaryLines = doc.splitTextToSize(analysis.summary, 180);
      doc.text(summaryLines, margin, y);
      y += summaryLines.length * 5 + 6;

      // Key Insights
      if (analysis.keyInsights?.length) {
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.keyInsights', 'Punti chiave'), margin, y);
        y += 6;
        doc.setFontSize(10);
        analysis.keyInsights.forEach(insight => {
          const lines = doc.splitTextToSize(`• ${insight}`, 175);
          if (y + lines.length * 5 > 280) { doc.addPage(); y = 20; }
          doc.text(lines, margin + 4, y);
          y += lines.length * 5 + 2;
        });
        y += 4;
      }

      // Average value
      if (analysis.avgValueRange) {
        doc.setFontSize(10);
        doc.text(`${t('search.aiAnalysis.avgValue', 'Valore medio')}: ${analysis.avgValueRange}`, margin, y);
        y += 8;
      }

      // Top Buyers table
      if (analysis.buyerAnalysis?.topBuyers?.length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.topBuyers', 'Enti appaltanti principali'), margin, y);
        y += 2;
        autoTable(doc, {
          startY: y,
          head: [['#', t('search.aiAnalysis.buyer', 'Ente'), t('search.aiAnalysis.tenders', 'Bandi')]],
          body: analysis.buyerAnalysis.topBuyers.map((b, i) => [i + 1, b.name, b.count]),
          margin: { left: margin },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 4;
        const buyerImg = await svgToDataUrl(buyerChartRef, 500, 200);
        if (buyerImg) {
          if (y + 55 > 280) { doc.addPage(); y = 20; }
          doc.addImage(buyerImg, 'PNG', margin, y, 180, 50);
          y += 55;
        }
      }

      // Top Winners table
      if (analysis.winnerAnalysis?.topWinners?.length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.topWinners', 'Ditte aggiudicatrici principali'), margin, y);
        y += 2;
        autoTable(doc, {
          startY: y,
          head: [['#', t('search.aiAnalysis.winner', 'Ditta'), t('search.aiAnalysis.awards', 'Aggiudicazioni')]],
          body: analysis.winnerAnalysis.topWinners.map((w, i) => [i + 1, w.name, w.count]),
          margin: { left: margin },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 4;
        const winnerImg = await svgToDataUrl(winnerChartRef, 500, 200);
        if (winnerImg) {
          if (y + 55 > 280) { doc.addPage(); y = 20; }
          doc.addImage(winnerImg, 'PNG', margin, y, 180, 50);
          y += 55;
        }
      }

      // Sector breakdown table
      if (analysis.sectorBreakdown && Object.keys(analysis.sectorBreakdown).length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.sectorBreakdown', 'Distribuzione settori'), margin, y);
        y += 2;
        autoTable(doc, {
          startY: y,
          head: [[t('search.aiAnalysis.sector', 'Settore'), t('search.aiAnalysis.tenders', 'Bandi')]],
          body: Object.entries(analysis.sectorBreakdown).map(([s, c]) => [s, c]),
          margin: { left: margin },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 4;
        const sectorImg = await svgToDataUrl(sectorChartRef, 400, 220);
        if (sectorImg) {
          if (y + 65 > 280) { doc.addPage(); y = 20; }
          doc.addImage(sectorImg, 'PNG', margin + 10, y, 160, 60);
          y += 65;
        }
      }

      // Top Opportunities
      if (analysis.topOpportunities?.length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.topOpportunities', 'Top opportunità'), margin, y);
        y += 2;
        autoTable(doc, {
          startY: y,
          head: [['#', t('search.aiAnalysis.opportunityTitle', 'Titolo'), t('search.aiAnalysis.opportunityReason', 'Motivo')]],
          body: analysis.topOpportunities.slice(0, 5).map((opp, i) => [i + 1, opp.title, opp.reason]),
          margin: { left: margin },
          styles: { fontSize: 9, cellWidth: 'wrap' },
          columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 80 }, 2: { cellWidth: 80 } },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // Recommendations
      if (analysis.recommendations?.length) {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.text(t('search.aiAnalysis.recommendations', 'Consigli'), margin, y);
        y += 6;
        doc.setFontSize(10);
        analysis.recommendations.forEach((rec, i) => {
          const lines = doc.splitTextToSize(`${i + 1}. ${rec}`, 175);
          if (y + lines.length * 5 > 280) { doc.addPage(); y = 20; }
          doc.text(lines, margin + 4, y);
          y += lines.length * 5 + 2;
        });
      }

      // Results list
      if (results.length > 0) {
        doc.addPage();
        y = 20;
        doc.setFontSize(14);
        doc.text(t('search.aiAnalysis.resultsList', 'Elenco bandi analizzati'), margin, y);
        y += 4;
        autoTable(doc, {
          startY: y,
          head: [['#', 'TED ID', t('search.aiAnalysis.resultTitle', 'Titolo'), t('search.aiAnalysis.buyer', 'Ente'), t('search.aiAnalysis.country', 'Paese'), t('search.aiAnalysis.value', 'Valore')]],
          body: results.slice(0, 25).map((r, i) => [
            i + 1,
            r.ted_notice_id,
            r.title?.length > 60 ? r.title.slice(0, 57) + '…' : (r.title || ''),
            r.buyer_name?.length > 30 ? r.buyer_name.slice(0, 27) + '…' : (r.buyer_name || ''),
            r.country || '',
            r.estimated_value ? `${r.estimated_value.toLocaleString()} ${r.currency || ''}` : '-',
          ]),
          margin: { left: margin },
          styles: { fontSize: 7, cellWidth: 'wrap' },
          columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 28 }, 2: { cellWidth: 60 }, 3: { cellWidth: 40 }, 4: { cellWidth: 14 }, 5: { cellWidth: 28 } },
        });
      }

      const slug = (searchQuery || '').replace(/[^a-zA-Z0-9À-ÿ]+/g, '-').replace(/^-|-$/g, '').slice(0, 50).toLowerCase() || 'risultati';
      doc.save(`analisi-ai-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
      trackActivity('export_pdf', { type: 'ai_analysis', query: searchQuery });
    } catch (err) {
      console.error('PDF export error:', err);
      toast({ variant: 'destructive', title: t('search.aiAnalysis.exportError', 'Errore esportazione PDF') });
    }
  }, [analysis, t, toast, results, svgToDataUrl]);

  if (results.length === 0 || isSearching) return null;

  return (
    <Card className="border-0 shadow-sm bg-gradient-to-r from-primary/5 to-accent/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {t('search.aiAnalysis.title', 'Analisi AI')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {status === 'success' && (
              <>
                <Button variant="ghost" size="sm" onClick={exportToPdf} className="h-8 px-2" title={t('search.aiAnalysis.exportPdf', 'Esporta PDF')}>
                  <Download className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => analyzeResults(true)} className="h-8 px-2">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </>
            )}
            {status === 'idle' && (
              <Button size="sm" onClick={() => analyzeResults()} className="gap-1">
                <Sparkles className="w-4 h-4" />
                {t('search.aiAnalysis.button', 'Analizza con AI')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {status === 'loading' && (
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </CardContent>
      )}

      {status === 'error' && (
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">
              {errorMessage || t('search.aiAnalysis.error', 'Errore durante l\'analisi')}
            </p>
            <Button variant="outline" size="sm" onClick={() => analyzeResults(true)}>
              {t('search.aiAnalysis.retry', 'Riprova')}
            </Button>
          </div>
        </CardContent>
      )}

      {status === 'success' && analysis && (
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <CardContent className="pt-0 pb-3 cursor-pointer hover:bg-muted/30 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <p className="text-sm text-foreground leading-relaxed">{analysis.summary}</p>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                )}
              </div>
            </CardContent>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0 space-y-4 border-t">
              {/* Key Insights */}
              {analysis.keyInsights?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    {t('search.aiAnalysis.keyInsights', 'Punti chiave')}
                  </h4>
                  <ul className="space-y-1">
                    {analysis.keyInsights.map((insight, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>{insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Average Value */}
              {analysis.avgValueRange && (
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-muted-foreground">{t('search.aiAnalysis.avgValue', 'Valore medio')}:</span>
                  <Badge variant="outline" className="font-mono">{analysis.avgValueRange}</Badge>
                </div>
              )}

              {/* Buyer Analysis Chart */}
              {analysis.buyerAnalysis?.topBuyers && analysis.buyerAnalysis.topBuyers.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    {t('search.aiAnalysis.topBuyers', 'Enti appaltanti principali')}
                  </h4>
                  {analysis.buyerAnalysis.concentration && (
                    <p className="text-xs text-muted-foreground mb-2 italic">{analysis.buyerAnalysis.concentration}</p>
                  )}
                  <div ref={buyerChartRef} className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analysis.buyerAnalysis.topBuyers.slice(0, 5).map(b => ({ ...b, name: b.name.length > 25 ? b.name.slice(0, 22) + '…' : b.name }))} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v: number) => [v, t('search.aiAnalysis.tenders', 'Bandi')]} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {analysis.buyerAnalysis.topBuyers.slice(0, 5).map((_, i) => (
                            <Cell key={i} className="fill-primary" style={{ opacity: 1 - i * 0.15 }} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Winner Analysis Chart */}
              {analysis.winnerAnalysis?.topWinners && analysis.winnerAnalysis.topWinners.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-accent-foreground" />
                    {t('search.aiAnalysis.topWinners', 'Ditte aggiudicatrici principali')}
                  </h4>
                  {analysis.winnerAnalysis.competitiveness && (
                    <p className="text-xs text-muted-foreground mb-2 italic">{analysis.winnerAnalysis.competitiveness}</p>
                  )}
                  <div ref={winnerChartRef} className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analysis.winnerAnalysis.topWinners.slice(0, 5).map(w => ({ ...w, name: w.name.length > 25 ? w.name.slice(0, 22) + '…' : w.name }))} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v: number) => [v, t('search.aiAnalysis.awards', 'Aggiudicazioni')]} />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {analysis.winnerAnalysis.topWinners.slice(0, 5).map((_, i) => (
                            <Cell key={i} className="fill-accent" style={{ opacity: 1 - i * 0.15 }} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top Opportunities */}
              {analysis.topOpportunities?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-green-500" />
                    {t('search.aiAnalysis.topOpportunities', 'Top opportunità')}
                  </h4>
                  <div className="space-y-2">
                    {analysis.topOpportunities.slice(0, 3).map((opp, i) => (
                      <div key={i} className="bg-background/50 rounded-lg p-3 border">
                        <p className="text-sm font-medium line-clamp-1">{opp.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{opp.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sector Breakdown Pie Chart */}
              {analysis.sectorBreakdown && Object.keys(analysis.sectorBreakdown).length > 0 && (() => {
                const COLORS = [
                  'hsl(var(--primary))',
                  'hsl(var(--accent))',
                  'hsl(var(--secondary))',
                  'hsl(210, 70%, 55%)',
                  'hsl(150, 60%, 45%)',
                  'hsl(30, 80%, 55%)',
                ];
                const pieData = Object.entries(analysis.sectorBreakdown).slice(0, 6).map(([name, value]) => ({ name, value }));
                return (
                  <div>
                    <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-primary" />
                      {t('search.aiAnalysis.sectorBreakdown', 'Distribuzione settori')}
                    </h4>
                    <div ref={sectorChartRef} className="h-52 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={75}
                            paddingAngle={3}
                            dataKey="value"
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1 }}
                          >
                            {pieData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v: number, name: string) => [v, name]} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}

              {/* Recommendations */}
              {analysis.recommendations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-purple-500" />
                    {t('search.aiAnalysis.recommendations', 'Consigli')}
                  </h4>
                  <ol className="space-y-1 list-decimal list-inside">
                    {analysis.recommendations.slice(0, 3).map((rec, i) => (
                      <li key={i} className="text-sm text-muted-foreground">{rec}</li>
                    ))}
                  </ol>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Card>
  );
}
